import React from 'react';
import { useTranslation } from 'react-i18next';
import { VoiceConnectionType } from '../../services/cxone-sdk.service';
import { isVoicePreferenceValid } from '../../hooks/settings/useVoicePreferenceStorage';
import { VoicePreferenceOptionsSection } from './VoicePreferenceOptionsSection';
import formStyles from './VoicePreferencesForm.module.css';

interface VoicePreferencesFormProps {
  selectedType: VoiceConnectionType;
  phoneNumber: string;
  stationId: string;
  rememberChoice: boolean;
  isConnecting: boolean;
  error?: string | null;
  onTypeChange: (type: VoiceConnectionType) => void;
  onPhoneNumberChange: (value: string) => void;
  onStationIdChange: (value: string) => void;
  onRememberChoiceChange: (value: boolean) => void;
  onConnect: () => void;
}

export function VoicePreferencesForm({
  selectedType,
  phoneNumber,
  stationId,
  rememberChoice,
  isConnecting,
  error,
  onTypeChange,
  onPhoneNumberChange,
  onStationIdChange,
  onRememberChoiceChange,
  onConnect,
}: VoicePreferencesFormProps) {
  const { t } = useTranslation();

  return (
    <div className={formStyles.content}>
      <div className={formStyles.sectionHeader}>
        <svg
          viewBox="0 0 24 24"
          width="28"
          height="28"
          fill="currentColor"
          className={formStyles.sectionIcon}
        >
          <path d="M20 15.5c-1.25 0-2.45-.2-3.57-.57a1.02 1.02 0 0 0-1.02.24l-2.2 2.2a15.045 15.045 0 0 1-6.59-6.59l2.2-2.21a.96.96 0 0 0 .25-1A11.36 11.36 0 0 1 8.5 4c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1 0 9.39 7.61 17 17 17 .55 0 1-.45 1-1v-3.5c0-.55-.45-1-1-1z" />
        </svg>
        <h2 className={formStyles.title}>{t('settings:voice.title')}</h2>
      </div>

      <p className={formStyles.subtitle}>{t('settings:voice.subtitle')}</p>

      {error && (
        <div className={formStyles.errorBanner}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <VoicePreferenceOptionsSection
        selectedType={selectedType}
        phoneNumber={phoneNumber}
        stationId={stationId}
        isConnecting={isConnecting}
        onTypeChange={onTypeChange}
        onPhoneNumberChange={onPhoneNumberChange}
        onStationIdChange={onStationIdChange}
      />

      <label className={formStyles.rememberChoice}>
        <input
          type="checkbox"
          checked={rememberChoice}
          onChange={(e) => onRememberChoiceChange(e.target.checked)}
          disabled={isConnecting}
        />
        <span>{t('settings:voice.rememberChoice')}</span>
      </label>

      <button
        className={formStyles.connectButton}
        onClick={onConnect}
        disabled={!isVoicePreferenceValid(selectedType, phoneNumber, stationId) || isConnecting}
      >
        {isConnecting ? (
          <>
            <span className={formStyles.spinner} />
            {t('common:status.connecting')}
          </>
        ) : (
          t('common:buttons.connect')
        )}
      </button>
    </div>
  );
}

export default VoicePreferencesForm;
